<template>
  <div class="pa-3">
    <v-row dense class="mb-1">
      <v-col cols="12" sm="6" md="3">
        <TarjetaKpi titulo="Órdenes en el periodo" :valor="fmtNum(totalOrdenes)" icono="mdi-clipboard-text-outline"
          color="indigo" :cargando="cargando" />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <TarjetaKpi titulo="Renglones del CSV" :valor="fmtNum(totalRenglones)" icono="mdi-format-list-numbered"
          color="blue-grey" :cargando="cargando" />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <TarjetaKpi titulo="Piezas a producir" :valor="fmtNum(cantidadTotal)" icono="mdi-package-variant-closed"
          color="teal" :cargando="cargando" />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <TarjetaKpi titulo="Sin cargar" :valor="fmtNum(sinCargar)" icono="mdi-alert-circle-outline"
          color="red" :cargando="cargando" :acento="sinCargar > 0" />
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" dense outlined class="mb-2">{{ error }}</v-alert>
    <v-alert v-else-if="mensaje" type="info" dense outlined class="mb-2">{{ mensaje }}</v-alert>

    <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px;">
      <v-text-field v-model="busqueda" dense outlined hide-details clearable placeholder="Buscar ODT, artículo, máquina…"
        prepend-inner-icon="mdi-magnify" style="max-width: 340px;" />
      <v-spacer />
      <span class="caption grey--text">{{ fmtNum(ordenes.length) }} renglones</span>
    </div>

    <v-data-table :headers="headers" :items="ordenes" :search="busqueda" :loading="cargando" dense
      :items-per-page="25" :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
      :height="altoTabla(ordenes.length)" :fixed-header="!!altoTabla(ordenes.length)"
      class="tabla" loading-text="Cargando... Espere, Por Favor."
      no-data-text="No hay órdenes de la última carga en este periodo.">
      <template v-slot:item.ClaveOT="{ item }">
        <div class="d-flex align-center" style="gap: 4px;">
          <span class="font-weight-medium">{{ item.ClaveOT }}</span>
          <v-chip v-if="item.Critica" x-small color="red" dark>Crítica</v-chip>
        </div>
      </template>

      <template v-slot:item.Cantidad="{ item }">{{ fmtNum(item.Cantidad) }}</template>
      <template v-slot:item.FechaCompromisoEntrega="{ item }">{{ fmtFecha(item.FechaCompromisoEntrega) }}</template>
      <template v-slot:item.FechaInicioProduccion="{ item }">
        <span :class="item.FechaInicioProduccion ? '' : 'red--text'">
          {{ fmtFecha(item.FechaInicioProduccion) }}
        </span>
      </template>

      <template v-slot:item.RateProgramado="{ item }">
        <span :class="item.RateValido ? '' : 'red--text font-weight-medium'">
          {{ item.RateProgramado || '0' }}
        </span>
      </template>

      <template v-slot:item.Estado="{ item }">
        <v-chip x-small :color="item.ExisteEnSistema ? 'green' : 'red'" dark
          :title="item.MotivoError || item.DetalleResultado || ''">
          {{ item.Estado }}
        </v-chip>
      </template>

      <template v-slot:item.EstadoProduccion="{ item }">
        {{ item.ExisteEnSistema ? (item.EstadoProduccion || '—') : '—' }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import axios from 'axios'
import TarjetaKpi from './TarjetaKpi.vue'
import { prop, fmtNum, fmtFecha, altoTabla, mensajeError } from './utils'

export default {
  name: 'CargaOtsInformacion',

  components: { TarjetaKpi },

  data() {
    return {
      cargando: false,
      error: '',
      mensaje: '',
      ordenes: [],
      // Totales del periodo que calcula el back (solo renglones "R" con fecha compromiso
      // dentro del rango).
      totalOrdenes: 0,
      totalRenglones: 0,
      cantidadTotal: 0,
      busqueda: '',
    }
  },

  computed: {
    // Renglones cuya ODT no llegó a existir en el sistema.
    sinCargar() {
      return this.ordenes.filter((o) => !o.ExisteEnSistema).length
    },

    headers() {
      return [
        { text: 'ODT', value: 'ClaveOT' },
        { text: 'Pos.', value: 'Posicion', align: 'end' },
        { text: 'Artículo', value: 'Articulo' },
        { text: 'Cantidad', value: 'Cantidad', align: 'end' },
        { text: 'Máquina', value: 'Maquina' },
        { text: 'Molde', value: 'Molde' },
        { text: 'Almacén', value: 'Almacen' },
        { text: 'Fecha compromiso', value: 'FechaCompromisoEntrega' },
        { text: 'Arranque', value: 'FechaInicioProduccion' },
        { text: 'Rate', value: 'RateProgramado', align: 'end' },
        { text: 'Estado', value: 'Estado' },
        { text: 'Producción', value: 'EstadoProduccion' },
      ]
    },
  },

  methods: {
    fmtNum,
    fmtFecha,
    altoTabla,

    // Lo llama el contenedor con el rango vigente de los filtros.
    async cargar(inicio, fin) {
      this.cargando = true
      this.$emit('cargando', true)
      this.error = ''
      this.mensaje = ''
      try {
        const p = new URLSearchParams()
        if (inicio) p.append('inicio', inicio)
        if (fin) p.append('fin', fin)

        const { data } = await axios.get(`/CargaOts/Ordenes?${p.toString()}`)
        this.ordenes = (prop(data, 'ordenes') || []).map((o) => this.normalizar(o))
        this.totalOrdenes = Number(prop(data, 'totalOrdenes')) || 0
        this.totalRenglones = Number(prop(data, 'totalRenglones')) || 0
        this.cantidadTotal = Number(prop(data, 'cantidadTotal')) || 0
        this.mensaje = prop(data, 'mensaje') || ''
        this.$emit('auditoria', prop(data, 'auditoria') || null)
        this.$emit('carga-en-curso', prop(data, 'cargaEnCurso') || null)
      } catch (e) {
        this.ordenes = []
        this.totalOrdenes = 0
        this.totalRenglones = 0
        this.cantidadTotal = 0
        this.error = mensajeError(e, 'No se pudieron consultar las órdenes de la última carga.')
        // eslint-disable-next-line no-console
        console.error('Error consultando las órdenes de la última carga', e)
      } finally {
        this.cargando = false
        this.$emit('cargando', false)
      }
    },

    normalizar(o) {
      const rate = prop(o, 'rateProgramado')
      return {
        ClaveOT: prop(o, 'claveOT'),
        Posicion: Number(prop(o, 'posicion')) || 0,
        Articulo: prop(o, 'articulo'),
        Cantidad: Number(prop(o, 'cantidad')) || 0,
        FechaCompromisoEntrega: prop(o, 'fechaCompromisoEntrega'),
        FechaInicioProduccion: prop(o, 'fechaInicioProduccion'),
        Almacen: prop(o, 'almacen'),
        Maquina: prop(o, 'maquina'),
        Molde: prop(o, 'molde'),
        RateProgramado: rate,
        // Sin rate > 0 no se puede calcular el tiempo de ciclo: se resalta en la tabla.
        RateValido: Number(rate) > 0,
        Critica: !!prop(o, 'critica'),
        DetalleResultado: prop(o, 'detalleResultado'),
        MotivoError: prop(o, 'motivoError'),
        ExisteEnSistema: !!prop(o, 'existeEnSistema'),
        EstadoProduccion: prop(o, 'estadoProduccion'),
        // Texto plano para que el buscador de la tabla encuentre por "cargada"/"no cargada".
        Estado: prop(o, 'existeEnSistema') ? 'Cargada' : 'No cargada',
      }
    },
  },
}
</script>

<style scoped>
.tabla >>> td,
.tabla >>> th {
  white-space: nowrap;
}
</style>
